import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import { getCookie } from "shared/auth/utils/cookieUtils";

type CuratorReviewHlsVideoProps = {
  videoId: number;
  title: string;
};

const CuratorReviewHlsVideo: React.FC<CuratorReviewHlsVideoProps> = ({
  videoId,
  title,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(videoId) || videoId <= 0) return;

    setError("");
    const src = `${getApiOrigin()}/gethlsstream/master/${videoId}`;

    if (Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup(xhr, requestUrl) {
          const target = new URL(requestUrl, window.location.href);
          const apiOrigin = new URL(getApiOrigin() || "/", window.location.href).origin;
          const token = getCookie("userToken");
          if (token && [apiOrigin, window.location.origin].includes(target.origin)) {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          }
        },
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          setError("Video playback failed. Check your connection or session and retry.");
          hls.destroy();
        }
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      return () => {
        hls.destroy();
      };
    }

    // Native HLS cannot attach the Authorization header to playlists/segments.
    // Do not leak long-lived session tokens through query strings or redirects.
    setError("Authenticated review playback requires a browser with Media Source support, such as Chrome, Edge or Firefox.");
    return () => { video.removeAttribute("src"); video.load(); };
  }, [videoId, attempt]);

  if (!Number.isFinite(videoId) || videoId <= 0) {
    return null;
  }

  return (
    <div className="curator-review-hls-block">
      <div className="curator-review-media-label">{title}</div>
      {error && <div role="alert">{error} <button type="button" onClick={() => setAttempt((value) => value + 1)}>Retry</button></div>}
      <video
        ref={videoRef}
        className="curator-review-video"
        controls
        playsInline
        preload="metadata"
      />
    </div>
  );
};

export default CuratorReviewHlsVideo;
